import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injectable, NgModule, inject, provideEnvironmentInitializer } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { KeycloakService } from './auth/keycloak.service';
import { KeycloakBearerInterceptor } from './auth/keycloak-bearer.interceptor';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { JWTAuthGuard } from './auth/jwtauth.guard';

import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { LandingComponent } from './components/landing/landing.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { UviComponent } from './components/uvi/uvi.component';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import {
	faAsterisk as fasAsterisk,
	faHotel as fasHotel,
	faSignOutAlt as fasSignOutAlt,
	faSlidersH as fasSliders,
	faStar as fasStar,
	faTachometerAlt as fasTacho,
	faUsers as fasUsers,
} from '@fortawesome/free-solid-svg-icons';

import { TranslocoRootModule } from './transloco/transloco-root.module';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { SettingsComponent } from './components/settings/settings.component';
import { TenantDetailCardsComponent } from './components/tenant-detail-cards/tenant-detail-cards.component';
import { ToastrModule } from 'ngx-toastr';
import { MAT_DATE_LOCALE, MatLine, MatRipple } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { ImageDialogComponent } from './components/image-dialog/image-dialog.component';
import { VexModule } from '../@vex/vex.module';
import { CustomLayoutModule } from './custom-layout/custom-layout.module';
import { CdkConnectedOverlay, CdkOverlayOrigin, OverlayContainer } from '@angular/cdk/overlay';
import { CookieService } from 'ngx-cookie-service';
import { TranslocoService } from '@jsverse/transloco';
import { AppThemes, ThemeService } from './services/theme.service';

import { Icons } from './util/Icons';
import { PermissionsComponent } from './components/permissions/permissions.component';
import { SupportAraappComponent } from './components/support-araapp/support-araapp.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ApiModule as RealEstateApiModule, Configuration as RealEstateApiConfiguration } from '@eadsysteme/ewsrealestateclient-v1';
import { ApiModule as RealEstateApiModuleV2, Configuration as RealEstateApiConfigurationV2 } from '@eadsysteme/ewsrealestateclient-v2';
import { Configuration as RealEstateApiConfigurationV3 } from '@eadsysteme/ewsrealestateclient-v3';
import { ApiModule as AuthApiModule, Configuration as AuthApiConfiguration } from '@eadsysteme/ewsauthclient-v1';
import { ApiModule as EnergyEfficiencyApiModule, Configuration as EnergyEfficiencyApiConfiguration } from '@eadsysteme/energyefficiencyclient-v1';
import { ApiModule as EnergyEfficiencyApiModuleV2, Configuration as EnergyEfficiencyApiConfigurationV2 } from '@eadsysteme/energyefficiencyclient-v2';
import { ApiModule as SettingsApiModule, Configuration as SettingsApiConfiguration } from '@eadsysteme/settingclient-v1';
import { ApiModule as PortalSettingsApiModule, Configuration as PortalSettingsApiConfiguration } from '@eadsysteme/portalsettingclient-v1';
import { ApiModule as OutboxApiModule, Configuration as OutboxApiConfiguration } from '@eadsysteme/outboxclient-v1';
import { ApiModule as ReportingApiModule, Configuration as ReportingApiConfiguration } from '@eadsysteme/reportingclient-v1';
import { ApiModule as TariffBillingApiModule, Configuration as TariffBillingApiConfiguration } from '@eadsysteme/tariffbillingclient-v1';
import { ApiModule as JobLogApiModule, Configuration as JobLogApiConfiguration } from '@eadsysteme/joblogclient-v1';
import { ApiModule as IssueTrackingApiModule, Configuration as IssueTrackingConfiguration } from '@eadsysteme/issuetrackingclient-v1';
import { ApiModule as ArticleApiModule, Configuration as ArticleApiConfiguration } from '@eadsysteme/articleclient-v1';
import { ApiModule as HardwareSecretApiModule, Configuration as HardwareSecretApiConfiguration } from '@eadsysteme/hardwaresecretclient-v1';
import { Configuration as HardwareSecretApiConfigurationV2 } from '@eadsysteme/hardwaresecretclient-v2';
import { ApiModule as DeviceMessageApiModule, Configuration as DeviceMessageApiConfiguration } from '@eadsysteme/devicemessageclient-v1';
import { ApiModule as ReadingValueApiModule, Configuration as ReadingValueApiConfiguration } from '@eadsysteme/readingvalueclient-v1';
import { ApiModule as DataExchangeApiModule, Configuration as DataExchangeApiConfiguration } from '@eadsysteme/dataexchangeclient-v1';
import { ApiModule as GatewaySetupApiModule, Configuration as GatewaySetupApiConfiguration } from '@eadsysteme/gatewaysetupclient-v1';
import { ApiModule as ContactApiModule, Configuration as ContactApiConfiguration } from '@eadsysteme/contactclient-v1';
import { ApiModule as WorkOrderApiModule, Configuration as WorkorderApiConfiguration } from '@eadsysteme/workorderclient-v3';
import { ApiModule as DeviceMonitoringApiModule, Configuration as DeviceMonitoringApiConfiguration } from '@eadsysteme/devicemonitoringclient-v1';
import { ApiModule as DeviceStockApiModule, Configuration as DeviceStockApiConfiguration } from '@eadsysteme/devicestockv2client-v1';
import { Configuration as ObjectStoreApiConfiguration } from '@eadsysteme/objectstoreclient-v1';
import { Configuration as MailRelayConfiguration } from '@eadsysteme/mailrelayclient-v1';
import { ApiModule as BillingApiModule, Configuration as BillingApiConfiguration } from '@eadsysteme/billingclient-v1';
import { Configuration as LegacyAuthCodeApiConfiguration, LegacyAuthCodeService } from '@eadsysteme/legacyauthcodeclient-v1';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { CommonModule, DatePipe, NgOptimizedImage, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { LegalNoticeComponent } from './components/legal-notice/legal-notice.component';
import { PrivacyComponent } from './components/privacy/privacy.component';
import { RefreshUviDialogComponent } from './components/uvi/refresh-uvi-dialog.component';
import { ReadingValueInfoDialogComponent } from './components/uvi/percent-display-per-energytype/reading-value-info-dialog/reading-value-info-dialog.component';
import { UviFooterComponent } from './components/settings/uvi-footer/uvi-footer.component';
import { UviLetterComponent } from './components/settings/uvi-letter/uvi-letter.component';
import { CustomPaginator } from './services/custom-paginator';
import { NgTooltipModule } from '@silmar/ng-tooltip';
import { MassPrintingFilterDialogComponent } from './components/mass-printing/filter-dialog/mass-printing-filter-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { AppendixDialogComponent } from './components/mass-printing/appendix-dialog/appendix-dialog.component';
import { PrintDialogComponent } from './components/mass-printing/print-dialog/print-dialog.component';
import { UploadDirective } from './directives/upload.directive';
import { VarDirective } from './directives/ng-var.directive';
import { DelayedSearchDirective } from './directives/delayed-search.directive';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { FilterDialogComponent } from './components/filter-dialog/filter-dialog.component';
import { RealEstateLegacyComponent } from './components/real-estate-legacy/real-estate-legacy.component';
import { TenantUnitDetailLegacyComponent } from './components/tenant-unit-detail-legacy/tenant-unit-detail-legacy.component';
import { ConsumptionInformationLegacyComponent } from './components/consumption-information-legacy/consumption-information-legacy.component';
import { OrderByPipe } from './util/OrderByPipe';
import './util/Prototypes';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { CdkDrag, CdkDragHandle, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerIntl, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { QRCodeComponent } from 'angularx-qrcode';
import { BarChartModule } from '@swimlane/ngx-charts';
import { TenantUnitDetailSettingsLegacyComponent } from './components/tenant-unit-detail-legacy/tenant-unit-detail-settings-legacy/tenant-unit-detail-settings-legacy.component';
import { LocaleHelper } from './util/LocaleHelper';
import { EwsDatepickerIntl } from './util/ews-datepicker-intl';
import { NgxTiptapModule } from 'ngx-tiptap';
import { CalculationInfoDialogComponent } from './components/uvi/percent-display-per-energytype/calculation-info-dialog/calculation-info-dialog.component';
import { PercentDisplayPerEnergyTypeComponent } from './components/uvi/percent-display-per-energytype/percent-display-per-energy-type.component';
import { ChartDisplayPerEnergytypeComponent } from './components/uvi/chart-display-per-energytype/chart-display-per-energytype.component';
import { ChartDisplayComponent } from './components/uvi/v1/chart-display/chart-display.component';
import { ColdwaterComponent } from './components/uvi/v1/chart-display/calculation-table/coldwater/coldwater.component';
import { CoolingComponent } from './components/uvi/v1/chart-display/calculation-table/cooling/cooling.component';
import { HeatingComponent } from './components/uvi/v1/chart-display/calculation-table/heating/heating.component';
import { WarmwaterComponent } from './components/uvi/v1/chart-display/calculation-table/warmwater/warmwater.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { UpdateProfileComponent } from './components/user-profile/update-profile/update-profile.component';
import { UpdateMailComponent } from './components/user-profile/update-mail/update-mail.component';
import { UpdatePasswordComponent } from './components/user-profile/update-password/update-password.component';
import { DeleteProfileComponent } from './components/user-profile/delete-profile/delete-profile.component';
import { MassPrintingComponent } from './components/mass-printing/mass-printing.component';
import { PermissionsTableComponent } from './components/permissions-table/permissions-table.component';
import { PermissionsDialogComponent } from './components/permissions-table/permissions-dialog/permissions-dialog.component';
import { DevelopmentComponent } from './components/development/development.component';
import { RealEstatesLegacyComponent } from './components/real-estates-legacy/real-estates-legacy.component';
import { DevSettingsComponent } from './components/settings/dev-settings/dev-settings.component';
import { SupportToolsComponent } from './components/settings/dev-settings/support-tools/support-tools.component';
import { UviLetterSettingsComponent } from './components/uvi-letter-settings/uvi-letter-settings.component';
import { FrontendToolsComponent } from './components/settings/dev-settings/frontend-tools/frontend-tools.component';
import { ConfigService } from './services/config/config.service';
import { Config } from './services/config/config';
import { AraPaginatorComponent } from './components/util/ara-paginator/ara-paginator.component';
import { AraSearchSelectComponent } from './components/util/ara-search-select/ara-search-select.component';
import { RolesViewComponent } from './components/tenant-unit-detail-legacy/roles-view/roles-view.component';

import { PolicyDialogComponent } from './components/policy-dialog/policy-dialog.component';
import { AraQrGeneratorComponent } from './components/util/ara-qr-generator/ara-qr-generator.component';
import { AraPasswordInputComponent } from './components/util/ara-password-input/ara-password-input.component';
import { TenantShareValueComponent } from './components/tenant-unit-detail-legacy/tenant-shares/tenant-share-value/tenant-share-value.component';
import { DocumentTransferSettingsComponent } from './components/settings/document-transfer-settings/document-transfer-settings.component';
import { AraEditorComponent } from './components/util/ara-editor/ara-editor.component';
import { AraEditorMenuComponent } from './components/util/ara-editor/ara-editor-menu/ara-editor-menu.component';
import { AraSpinnerButtonComponent } from './components/util/ara-spinner-button/ara-spinner-button.component';
import { TenantunitComponent } from './components/billings/billing/tenantunit/tenantunit.component';
import { TenantUnitReferenceComponent } from './components/billings/billing/tenantunit/billing-instances-master/billing-instance/tenant-unit-reference.component';
import { AraPopoverComponent } from './components/util/ara-popover/popover.component';
import { AraSoonBadgeComponent } from './components/util/ara-soon-badge/ara-soon-badge.component';
import { SplashScreenService } from '../@vex/services/splash-screen.service';

@Injectable()
export class NoCacheHeadersInterceptor implements HttpInterceptor {
	intercept(req: HttpRequest<any>, next: HttpHandler) {
		const authReq = req.clone({
			setHeaders: {
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				Pragma: 'no-cache',
				Expires: '0',
			},
		});
		return next.handle(authReq);
	}
}

function initializeKeycloak(keycloak: KeycloakService, configService: ConfigService, legacyAuthCodeService: LegacyAuthCodeService) {
	return () =>
		configService.loadConfig().then(() => {
			const searchParams = new URLSearchParams(globalThis.location.search);
			const authCode = searchParams.get('authcode') ?? searchParams.get('authCode');

			if (globalThis.location.pathname.includes('permissions') && authCode) {
				return handleLegacyAuthCode(authCode, legacyAuthCodeService, configService, keycloak);
			}

			return getKeycloak(keycloak);
		});
}

function handleLegacyAuthCode(
	authCode: string,
	legacyAuthCodeService: LegacyAuthCodeService,
	configService: ConfigService,
	keycloak: KeycloakService
): Promise<any> {
	return new Promise<void>((resolve) => {
		legacyAuthCodeService.getPermissionTypeByAuthCode(authCode).subscribe({
			next: (permissionType: string) => {
				if (permissionType === 'RESIDENT') {
					redirectToResidentPortal(authCode, configService);
					// Don't resolve - let the redirect happen
				} else {
					// For non-resident types, proceed with normal Keycloak init
					resolve();
				}
			},
			error: () => {
				resolve(); // Proceed with Keycloak on error
			},
		});
	}).then(() => getKeycloak(keycloak));
}

function redirectToResidentPortal(authCode: string, configService: ConfigService): void {
	const currentPath = globalThis.location.pathname;
	const searchParams = new URLSearchParams(globalThis.location.search);
	globalThis.location.href = `${configService.getAraAppUrl()}${currentPath}?${searchParams.toString()}`;
}

function getKeycloak(keycloak: KeycloakService) {
	// The instance is created + configured (url/realm/clientId) by provideKeycloak() in main.ts.
	// We only trigger the explicit init here (provideKeycloak was called without initOptions so it
	// does NOT auto-init), preserving the order: legacy-auth-code/resident check first, then login.
	const instance = keycloak.getKeycloakInstance();
	return instance.init({
		onLoad: 'login-required',
		checkLoginIframe: false,
		token: instance.token ?? '',
		refreshToken: instance.refreshToken ?? '',
	});
}

export function initializeAppConfigService(configService: ConfigService) {
	return () => configService.loadConfig();
}

const cookieConfig: NgcCookieConsentConfig = {
	cookie: {
		domain: globalThis.location.hostname, // or 'your.domain.com'
	},
	position: 'bottom',
	theme: 'classic',
	palette: {
		popup: {
			background: '#000000',
			text: '#ffffff',
		},
		button: {
			background: '#f1d600',
			text: '#000000',
		},
	},
	type: 'info',
	content: {
		message: 'Diese Webseite nutzt Cookies um eine bestmögliche Darstellung zu gewährleisten.',
		dismiss: 'Verstanden',
		allow: 'Verstanden',
		deny: 'Ablehnen',
		link: 'Mehr erfahren',
		href: 'https://cookiesandyou.com',
		policy: 'Cookie Policy',
	},
};

@NgModule({
	declarations: [],
	imports: [
		AppRoutingModule,
		ArticleApiModule,
		AuthApiModule,
		CommonModule,
		ContactApiModule,
		WorkOrderApiModule,
		DataExchangeApiModule,
		DeviceMessageApiModule,
		DeviceMonitoringApiModule,
		DeviceStockApiModule,
		EnergyEfficiencyApiModule,
		EnergyEfficiencyApiModuleV2,
		FlexLayoutModule,
		GatewaySetupApiModule,
		BillingApiModule,
		HardwareSecretApiModule,
		IssueTrackingApiModule,
		JobLogApiModule,
		MatSnackBarModule,
		NgcCookieConsentModule.forRoot(cookieConfig),
		OutboxApiModule,
		PortalSettingsApiModule,
		ReadingValueApiModule,
		RealEstateApiModule,
		RealEstateApiModuleV2,
		ReportingApiModule,
		TariffBillingApiModule,
		RouterModule,
		SettingsApiModule,
		ToastrModule.forRoot(),
		TranslocoRootModule,
		// Vex
		CdkDropList,
		CustomLayoutModule,
		FormsModule,
		HttpClientModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatCheckboxModule,
		MatChipsModule,
		MatDatepickerModule,
		MatDialogModule,
		MatDividerModule,
		MatExpansionModule,
		MatFormFieldModule,
		MatIconModule,
		MatInputModule,
		MatListModule,
		MatMenuModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		MatRadioModule,
		MatSelectModule,
		MatSlideToggleModule,
		MatSortModule,
		MatTableModule,
		MatTabsModule,
		MatTooltipModule,
		NgTooltipModule,
		NgxMatSelectSearchModule,
		ReactiveFormsModule,
		VexModule,
		MatCardModule,
		QRCodeComponent,
		BarChartModule,
		CdkDropListGroup,
		CdkDrag,
		MatLine,
		MatRipple,
		NgxTiptapModule,
		NgOptimizedImage,
		CommonModule,
		CdkConnectedOverlay,
		CdkOverlayOrigin,
		FooterComponent,
		TenantDetailCardsComponent,
		RealEstatesLegacyComponent,
		HeaderComponent,
		UploadDirective,
		DelayedSearchDirective,
		OrderByPipe,
		PermissionsComponent,
		AraPaginatorComponent,
		AraSearchSelectComponent,
		RolesViewComponent,
		CdkDragHandle,
		AraPasswordInputComponent,
		TenantShareValueComponent,
		DocumentTransferSettingsComponent,
		AraSpinnerButtonComponent,
		SupportAraappComponent,
		TenantunitComponent,
		TenantUnitReferenceComponent,
		AraPopoverComponent,
		AraSoonBadgeComponent,
		LandingComponent,
		DashboardComponent,
		SettingsComponent,
		UviComponent,
		ConfirmationDialogComponent,
		RefreshUviDialogComponent,
		ImageDialogComponent,
		LegalNoticeComponent,
		PrivacyComponent,
		TenantUnitDetailSettingsLegacyComponent,
		ConsumptionInformationLegacyComponent,
		FilterDialogComponent,
		MassPrintingComponent,
		MassPrintingFilterDialogComponent,
		UviFooterComponent,
		UviLetterComponent,
		AppendixDialogComponent,
		PrintDialogComponent,
		PermissionsDialogComponent,
		PolicyDialogComponent,
		PermissionsTableComponent,
		VarDirective,
		RealEstateLegacyComponent,
		TenantUnitDetailLegacyComponent,
		ConsumptionInformationLegacyComponent,
		ReadingValueInfoDialogComponent,
		CalculationInfoDialogComponent,
		PercentDisplayPerEnergyTypeComponent,
		ChartDisplayPerEnergytypeComponent,
		ChartDisplayComponent,
		ColdwaterComponent,
		CoolingComponent,
		HeatingComponent,
		WarmwaterComponent,
		UserProfileComponent,
		UpdateProfileComponent,
		UpdateMailComponent,
		UpdatePasswordComponent,
		DeleteProfileComponent,
		DevelopmentComponent,
		DevSettingsComponent,
		SupportToolsComponent,
		UviLetterSettingsComponent,
		FrontendToolsComponent,
		AraQrGeneratorComponent,
		AraEditorComponent,
		AraEditorMenuComponent,
	],
	providers: [
		DatePipe,
		ConfigService,
		// Lazyload configService to make sure, correct config is read at the correct time
		{
			provide: LegacyAuthCodeApiConfiguration,
			useFactory: (configService: ConfigService) => {
				const config = new LegacyAuthCodeApiConfiguration();
				Object.defineProperty(config, 'basePath', {
					get() {
						return configService.getLegacyAuthCodeUrl();
					},
					enumerable: true,
					configurable: true,
				});
				return config;
			},
			deps: [ConfigService],
			multi: false,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initializeKeycloak,
			multi: true,
			deps: [KeycloakService, ConfigService, LegacyAuthCodeService],
		},
		{
			provide: APP_INITIALIZER,
			useFactory: () => () => {},
			multi: true,
			deps: [SplashScreenService],
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: NoCacheHeadersInterceptor,
			multi: true,
		},
		{
			// Replaces keycloak-angular's removed enableBearerInterceptor: adds + refreshes the
			// bearer token on outgoing requests (see keycloak-bearer.interceptor.ts).
			provide: HTTP_INTERCEPTORS,
			useClass: KeycloakBearerInterceptor,
			multi: true,
		},
		// Übersetzt die (englischen) Material-Datepicker-Kalender-Labels via Transloco,
		// u. a. die als Tooltip gerenderten Monat-/Jahr-Navigations-Buttons.
		{ provide: MatDatepickerIntl, useClass: EwsDatepickerIntl },
		{ provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
		LocaleHelper,
		JWTAuthGuard,
		Icons,
		{
			provide: RealEstateApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new RealEstateApiConfiguration({
					basePath: configService.getRealEstateUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: MatPaginatorIntl,
			useClass: CustomPaginator,
		},
		{
			provide: RealEstateApiConfigurationV2,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new RealEstateApiConfigurationV2({
					basePath: configService.getRealEstateUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: RealEstateApiConfigurationV3,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new RealEstateApiConfigurationV3({
					basePath: configService.getRealEstateUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: AuthApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new AuthApiConfiguration({
					basePath: configService.getAuthUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: EnergyEfficiencyApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new EnergyEfficiencyApiConfiguration({
					basePath: configService.getEnergyEfficiencyUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: EnergyEfficiencyApiConfigurationV2,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new EnergyEfficiencyApiConfigurationV2({
					basePath: configService.getEnergyEfficiencyUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: SettingsApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new SettingsApiConfiguration({
					basePath: configService.getSettingsUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: PortalSettingsApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new PortalSettingsApiConfiguration({
					basePath: configService.getPortalSettingUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: OutboxApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new OutboxApiConfiguration({
					basePath: configService.getOutboxUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: DataExchangeApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new DataExchangeApiConfiguration({
					basePath: configService.getDataExchangeUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: JobLogApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new JobLogApiConfiguration({
					basePath: configService.getJobLogUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: ReportingApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new ReportingApiConfiguration({
					basePath: configService.getReportingUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: TariffBillingApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new TariffBillingApiConfiguration({
					basePath: configService.getTariffBillingUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: IssueTrackingConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new IssueTrackingConfiguration({
					basePath: configService.getIssueTrackingUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: ArticleApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new ArticleApiConfiguration({
					basePath: configService.getArticleUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: ReadingValueApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new ReadingValueApiConfiguration({
					basePath: configService.getReadingValueUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: HardwareSecretApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new HardwareSecretApiConfiguration({
					basePath: configService.getHardwareSecretUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: HardwareSecretApiConfigurationV2,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new HardwareSecretApiConfigurationV2({
					basePath: configService.getHardwareSecretUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: DeviceMessageApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new DeviceMessageApiConfiguration({
					basePath: configService.getDeviceMessageUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: GatewaySetupApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new GatewaySetupApiConfiguration({
					basePath: configService.getGatewaySetupUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: ContactApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new ContactApiConfiguration({
					basePath: configService.getContactUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: DeviceMonitoringApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new DeviceMonitoringApiConfiguration({
					basePath: configService.getDeviceMonitoringUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: WorkorderApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new WorkorderApiConfiguration({
					basePath: configService.getWorkOrderUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: DeviceStockApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new DeviceStockApiConfiguration({
					basePath: configService.getDeviceStockUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: BillingApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new BillingApiConfiguration({
					basePath: configService.getBillingUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: ObjectStoreApiConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new ObjectStoreApiConfiguration({
					basePath: configService.getObjectStoreUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		{
			provide: MailRelayConfiguration,
			useFactory: (authService: KeycloakService, configService: ConfigService) =>
				new MailRelayConfiguration({
					basePath: configService.getMailRelayUrl(),
				}),
			deps: [KeycloakService, ConfigService],
			multi: false,
		},
		provideEnvironmentInitializer(() => {
			// Eager startup side-effects formerly in the AppModule constructor. Run as an environment
			// initializer so they still fire under bootstrapApplication + importProvidersFrom (the module
			// class itself is only instantiated lazily and would otherwise never run them).
			const library = inject(FaIconLibrary);
			const overlayContainer = inject(OverlayContainer);
			const themeService = inject(ThemeService);
			const translocoService = inject(TranslocoService);
			const translocoLocaleService = inject(TranslocoLocaleService);
			const cookieService = inject(CookieService);

			library.addIcons(fasStar, farStar, fasAsterisk, fasSignOutAlt, fasHotel, fasUsers, fasTacho, fasSliders);
			themeService.getTheme().subscribe((data) => {
				if (data === AppThemes.light) {
					overlayContainer.getContainerElement().classList.remove(AppThemes.dark);
				} else {
					overlayContainer.getContainerElement().classList.add(AppThemes.dark);
				}
			});
			const locale = cookieService.get('locale');
			translocoService.setActiveLang(locale);
			translocoLocaleService.setLocale(LocaleHelper.languageToLocale(locale));
			registerLocaleData(localeEn);
			registerLocaleData(localeDe);
			if (translocoService.getActiveLang() === 'en') {
				cookieConfig.content = {
					message: 'This website uses cookies to ensure you get the best experience on our website.',
					dismiss: 'Got it',
					allow: 'Got it',
					deny: 'No',
					link: 'Learn more',
				};
			} else {
				cookieConfig.content = {
					message: 'Diese Webseite nutzt Cookies um eine bestmögliche Darstellung zu gewährleisten.',
					dismiss: 'Verstanden',
					allow: 'Verstanden',
					deny: 'Ablehnen',
					link: 'Mehr erfahren',
				};
			}
		}),
	],
	exports: [TenantDetailCardsComponent, RealEstatesLegacyComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
